import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtNum, fmtDate, GROUP_COLORS, SERIES_COLORS } from "../lib/api";
import { useAuth } from "../lib/auth";
import TrendChart from "./TrendChart";
import PrintButton from "./PrintButton";
import MedicalHistoryReport from "./MedicalHistoryReport";
import ParamInfo from "./ParamInfo";
import InjuriesPanel from "./InjuriesPanel";
import InjuryFormModal from "./InjuryFormModal";
import IronTriadChart from "./IronTriadChart";
import WellnessSection from "./WellnessSection";
import AthleteSubTabs from "./AthleteSubTabs";
import WhatsAppShareReportButton from "./WhatsAppShareReportButton";
import { TrendingUp, Calendar, Stethoscope, FileText, Activity, ArrowUp, ArrowDown, ArrowRight, Check } from "lucide-react";

const STATUS_PILL = {
  ok:   { cls: "s-ok",   label: "OK" },
  low:  { cls: "s-low",  label: "LOW" },
  high: { cls: "s-high", label: "HIGH" },
  na:   { cls: "s-na",   label: "—" },
};

// Severity → tailwind class palette for the tile background/border/text.
// Uses primary green/orange/red hues (not muted emerald/amber/rose) so the
// blocks pop at a glance from across a tablet.
const SEVERITY_TILE = {
  ok:       "bg-green-500/30 border-green-400/70 text-green-50",
  mild:     "bg-orange-500/35 border-orange-400/80 text-orange-50",
  moderate: "bg-red-500/40 border-red-400/80 text-red-50",
  severe:   "bg-red-700/70 border-red-400 text-white",
  na:       "bg-slate-700/30 border-slate-600/40 text-slate-300",
};
const SEVERITY_BADGE_LABEL = {
  ok: "IN RANGE", mild: "MILD", moderate: "OUT OF RANGE", severe: "SEVERE",
  na: "NO RANGE",
};

// Classify how far outside the healthy range a value sits, using the
// range width as the denominator so different markers use a comparable
// severity scale. Mirrors the ResponseTracker logic.
function classifySeverity(value, low, high) {
  if (value == null || Number.isNaN(value)) return { severity: "na", side: null, deviationPct: 0 };
  const hasBoth = low != null && high != null;
  const width   = hasBoth ? Math.max(high - low, 1e-6) : null;
  if (low != null && value < low) {
    const gap = low - value;
    const dev = width ? gap / width : (low > 0 ? gap / low : 1);
    return {
      severity: dev < 0.20 ? "mild" : dev < 0.50 ? "moderate" : "severe",
      side: "low",
      deviationPct: dev * 100,
    };
  }
  if (high != null && value > high) {
    const gap = value - high;
    const dev = width ? gap / width : (high > 0 ? gap / high : 1);
    return {
      severity: dev < 0.20 ? "mild" : dev < 0.50 ? "moderate" : "severe",
      side: "high",
      deviationPct: dev * 100,
    };
  }
  if (low != null || high != null) return { severity: "ok", side: null, deviationPct: 0 };
  return { severity: "na", side: null, deviationPct: 0 };
}

export default function SailorDetail({ sailorId, parameters, injuryConstants, onInjuriesChanged, activeTab, onSelectTab }) {
  const { hasPerm } = useAuth();
  const canPrint = hasPerm("print");
  const [detail, setDetail] = useState(null);
  const [selectedParams, setSelectedParams] = useState(["hb", "iron", "vit_d", "b12"]);
  const [showReport, setShowReport] = useState(false);
  const [reportMode, setReportMode] = useState("full");
  const [injuryAddOpen, setInjuryAddOpen] = useState(false);
  const [injuryReloadKey, setInjuryReloadKey] = useState(0);
  const trendRef = useRef(null);
  const snapshotRef = useRef(null);

  useEffect(() => {
    if (!sailorId) return;
    api.get(`/sailors/${sailorId}`).then((r) => setDetail(r.data));
  }, [sailorId]);

  // For the snapshot header and "N tests" counter we want real lab tests
  // (records with bloodwork params). Biometrics, supplements, dewormings,
  // and advice-only consultations don't belong in the trend snapshot.
  const labRecords = useMemo(
    () => (detail?.records || []).filter(
      (r) => r.type !== "biometric"
          && r.type !== "supplement"
          && r.type !== "deworming"
          && r.type !== "wellness"
          && r.params
          && Object.keys(r.params).length > 0,
    ),
    [detail],
  );

  // Per-class percentile baseline so each snapshot pill can show "30th
  // percentile in Opti A" — cohort-relative context next to the absolute
  // value. Fetched once per mount (small payload, rarely changes).
  const [percentiles, setPercentiles] = useState({});
  useEffect(() => {
    api.get("/squad/percentiles")
      .then((r) => setPercentiles(r.data || {}))
      .catch(() => setPercentiles({}));
  }, []);

  // Determine the athlete's primary class for percentile lookup. Take the
  // first item if they sail multiple — close enough for a chip.
  const athleteClass = useMemo(() => {
    const sub = (detail?.subgroup || "").trim();
    if (sub) {
      const first = sub.split(/[,/&]/)[0]?.trim();
      if (first) return first;
    }
    return detail?.group || null;
  }, [detail]);

  const chartData = useMemo(() => {
    if (!detail) return [];
    return detail.records.map((r) => {
      const row = { date: fmtDate(r.date) };
      const params = r.params || {};
      selectedParams.forEach((k) => {
        if (params[k] != null) row[k] = params[k];
      });
      return row;
    });
  }, [detail, selectedParams]);

  // Stable sailor summary passed to InjuriesPanel / InjuryFormModal —
  // a fresh `{ id, name }` on every render was flagged by the code
  // reviewer as a re-render culprit. Both children are heavy (network
  // calls + form state) so a memoised ref matters.
  const sailorSummary = useMemo(
    () => ({ id: sailorId, name: detail?.name || "" }),
    [sailorId, detail?.name],
  );

  const series = useMemo(() => {
    const map = Object.fromEntries((parameters || []).map((p) => [p.key, p]));
    return selectedParams.map((k, i) => ({
      key: k,
      name: map[k]?.label || k,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      // Per-series range so TrendChart can draw colour-matched dashed
      // limit lines for each parameter when multiple are selected.
      range: map[k] ? { low: map[k].range_low, high: map[k].range_high } : undefined,
    }));
  }, [selectedParams, parameters]);

  // Most-recent lab record for the "latest snapshot" tiles.
  const latest = labRecords[labRecords.length - 1];

  // Pre-compute the tile array once per render pass — filter + cohort
  // percentile math used to run inline on every parent re-render
  // (~40 params × cohort lookup). Flagged in the code-review perf pass.
  const previous = labRecords[labRecords.length - 2];
  const snapshotCards = useMemo(() => {
    if (!latest) return [];
    return (parameters || [])
      .filter((p) => latest.params?.[p.key] != null)
      .map((p) => {
        const v = latest.params?.[p.key];
        const st = latest.status?.[p.key] || "na";
        const pill = STATUS_PILL[st];
        // Severity — how far outside the healthy range.
        const cls = classifySeverity(v, p.range_low, p.range_high);
        // Trend vs the immediately-prior lab reading, if any.
        const prevV = previous?.params?.[p.key];
        let trend = "flat"; // "up" | "down" | "flat"
        let deltaFromPrev = null;
        if (typeof v === "number" && typeof prevV === "number") {
          const raw = v - prevV;
          deltaFromPrev = raw;
          const denom = Math.max(Math.abs(prevV), 1e-6);
          const pctChange = Math.abs(raw) / denom;
          if (pctChange >= 0.03) trend = raw > 0 ? "up" : "down";
        }
        const cohort = athleteClass && percentiles?.[athleteClass]?.[p.key];
        let pctChip = null;
        if (cohort && typeof v === "number") {
          const { p10, p50, p90, n } = cohort;
          let bucket;
          if (v <= p10) bucket = "≤10th";
          else if (v <= p50) bucket = `${Math.round(10 + 40 * ((v - p10) / Math.max(p50 - p10, 0.001)))}th`;
          else if (v <= p90) bucket = `${Math.round(50 + 40 * ((v - p50) / Math.max(p90 - p50, 0.001)))}th`;
          else bucket = "≥90th";
          pctChip = (
            <div
              className="text-[9px] mt-1 font-mono-data opacity-70"
              title={`${athleteClass} cohort (n=${n}): p10=${p10}, p50=${p50}, p90=${p90}`}
            >
              {bucket} pctl · {athleteClass}
            </div>
          );
        }
        return { p, v, pill, pctChip, cls, trend, prevV, deltaFromPrev };
      });
  }, [latest, previous, parameters, athleteClass, percentiles]);

  if (!sailorId) {
    return (
      <div className="card p-10 text-center text-slate-400">
        <TrendingUp size={32} className="mx-auto mb-3 text-slate-600" />
        Pick an athlete from the list to see longitudinal trends
      </div>
    );
  }

  if (!detail) {
    return <div className="card p-10 text-center text-slate-500">Loading athlete...</div>;
  }

  return (
    <div className="space-y-5 fade-up" data-testid="sailor-detail">
      {/* Header */}
      <div className="card p-5 md:p-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Athlete profile</div>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-1" data-testid="sailor-name">
            {detail.name}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{
                background: `${GROUP_COLORS[detail.group] || "#94A3B8"}22`,
                color: GROUP_COLORS[detail.group] || "#94A3B8",
                border: `1px solid ${GROUP_COLORS[detail.group] || "#94A3B8"}66`,
              }}
            >
              {detail.group}
            </span>
            {detail.subgroup && (
              <span className="px-2.5 py-0.5 rounded-full text-xs border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 font-mono-data">
                {detail.subgroup}
              </span>
            )}
            <span className="text-slate-400 flex items-center gap-1.5">
              <Calendar size={14} /> {labRecords.length} tests
            </span>
            {labRecords.length > 0 && (
              <span className="text-slate-400">
                {fmtDate(labRecords[0].date)} → {fmtDate(latest.date)}
              </span>
            )}
          </div>
          {onSelectTab && (
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2"
                 data-testid="athlete-actions-reports">
              <span className="text-sm font-semibold uppercase tracking-wider text-slate-200 whitespace-nowrap">
                Trends, AI and Reports
              </span>
              <AthleteSubTabs activeTab={activeTab} onSelectTab={onSelectTab} />
            </div>
          )}
        </div>
        {canPrint && (
          <div className="flex flex-col gap-1.5 shrink-0">
            <button
              className="pill active"
              onClick={() => { setReportMode("full"); setShowReport(true); }}
              data-testid="print-history-btn"
              title="Open the full medical history (printable / save as PDF)"
            >
              <FileText size={14} /> Print full history
            </button>
            <button
              className="pill"
              onClick={() => { setReportMode("latest"); setShowReport(true); }}
              data-testid="print-latest-btn"
              title="Print only the latest test, latest biometrics, and baseline"
            >
              <FileText size={14} /> Print latest
            </button>
            <button
              className="pill"
              onClick={() => { setReportMode("issues"); setShowReport(true); }}
              data-testid="print-issues-btn"
              title="Print only the out-of-range parameters from the most recent lab"
            >
              <FileText size={14} /> Print issues
            </button>
            <button
              className="pill"
              onClick={() => setInjuryAddOpen(true)}
              data-testid="log-injury-btn-trend"
              style={{ borderColor: "#FB718566", color: "#FCA5A5" }}
              title="Log an injury, illness or first-aid event"
            >
              <Activity size={14} /> Log injury/illness
            </button>
          </div>
        )}
      </div>

      {/* Latest snapshot pills */}
      {latest && (
        <div className="card p-5 md:p-6" data-testid="latest-snapshot" ref={snapshotRef}>
          <div className="flex items-center justify-between mb-4 gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Latest test snapshot</div>
              <div className="font-display text-lg text-slate-100 mt-0.5">{fmtDate(latest.date)}</div>
            </div>
            <div className="flex items-center gap-2">
              {(latest.dr || latest.hospital) && (
                <div className="text-right text-xs text-slate-400 max-w-xs">
                  {latest.hospital && <div className="font-mono-data">{latest.hospital}</div>}
                  {latest.dr && (
                    <div className="flex items-center gap-1 justify-end mt-1">
                      <Stethoscope size={12} /> {latest.dr}
                    </div>
                  )}
                </div>
              )}
              <WhatsAppShareReportButton
                getNode={() => snapshotRef.current}
                filename={`${detail?.name || "athlete"}-latest-snapshot-${latest.date || ""}.png`}
                athleteName={detail?.name || "athlete"}
                reportLabel="latest test snapshot"
                getMessageLines={() => {
                  const flagged = snapshotCards.filter(({ cls }) => cls.side).length;
                  return [
                    `Test date: ${fmtDate(latest.date)}`,
                    `${snapshotCards.length} parameters tested${flagged > 0 ? ` · ${flagged} outside range` : " · all in range"}`,
                    latest.hospital ? `Lab: ${latest.hospital}` : "",
                  ];
                }}
                familyContacts={detail?.family_contacts || []}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {snapshotCards.map(({ p, v, pill, pctChip, cls, trend, prevV, deltaFromPrev }) => {
              // Range string shown on every tile so a coach doesn't
              // have to hover the info icon to know the acceptable band.
              const rangeStr = (p.range_low != null && p.range_high != null)
                ? `${fmtNum(p.range_low)}–${fmtNum(p.range_high)}`
                : (p.range_low != null ? `≥ ${fmtNum(p.range_low)}`
                    : (p.range_high != null ? `≤ ${fmtNum(p.range_high)}` : "no range set"));
              // Big severity arrow inside the value block — up when the
              // reading is above upper bound, down when below lower,
              // sideways when it sits inside range.
              const SeverityIcon = cls.side === "high" ? ArrowUp
                : cls.side === "low" ? ArrowDown
                : (cls.severity === "ok" ? ArrowRight : null);
              // Trend arrow vs previous reading — separate from severity.
              const TrendIcon = trend === "up" ? ArrowUp
                : trend === "down" ? ArrowDown : ArrowRight;
              const trendTone = trend === "up" ? "text-cyan-300"
                : trend === "down" ? "text-orange-300"
                : "text-slate-500";
              const isSelected = selectedParams.includes(p.key);
              return (
                <div
                  key={p.key}
                  className={`rounded-xl border p-3 ${SEVERITY_TILE[cls.severity] || SEVERITY_TILE.na} cursor-pointer transition-transform hover:-translate-y-0.5 ${
                    isSelected ? "ring-2 ring-cyan-400/60" : ""
                  }`}
                  onClick={() => {
                    if (selectedParams.includes(p.key)) {
                      setSelectedParams(selectedParams.filter((x) => x !== p.key));
                    } else if (selectedParams.length < 6) {
                      setSelectedParams([...selectedParams, p.key]);
                    }
                  }}
                  data-testid={`snapshot-${p.key}`}
                  title={
`${p.label}
Value: ${fmtNum(v, 3)} ${p.unit || ""}
Range: ${rangeStr} ${p.unit || ""}
${cls.side ? `${cls.deviationPct.toFixed(0)}% ${cls.side === "high" ? "above upper" : "below lower"} bound (${SEVERITY_BADGE_LABEL[cls.severity]})` : `In healthy range (${SEVERITY_BADGE_LABEL[cls.severity]})`}
${prevV != null ? `Previous test: ${fmtNum(prevV, 3)} · Δ ${deltaFromPrev > 0 ? "+" : ""}${fmtNum(deltaFromPrev, 3)}` : "No prior reading"}
Click to toggle on the trend chart.`
                  }
                >
                  {/* Label + info icon */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] uppercase tracking-wider opacity-80 font-semibold">{p.label}</div>
                    <ParamInfo
                      paramKey={p.key}
                      low={p.range_low}
                      high={p.range_high}
                    />
                  </div>
                  {/* Big value with severity arrow */}
                  <div className="flex items-baseline justify-between mt-1 gap-2">
                    <div className="flex items-baseline gap-1.5 min-w-0">
                      {SeverityIcon && (
                        <SeverityIcon
                          size={18}
                          strokeWidth={3.5}
                          className={`shrink-0 self-center ${
                            cls.severity === "ok" ? "text-green-100 opacity-80"
                            : cls.severity === "severe" ? "text-white"
                            : cls.severity === "moderate" ? "text-red-50"
                            : "text-orange-50"
                          }`}
                        />
                      )}
                      <div className="font-display font-extrabold text-2xl leading-none">
                        {fmtNum(v, 2)}
                      </div>
                    </div>
                    <div className="text-[10px] opacity-80 font-mono-data shrink-0">{p.unit}</div>
                  </div>
                  {/* Row: range (left) + severity badge (right) */}
                  <div className="flex items-center justify-between mt-1 text-[9.5px] font-mono-data gap-1">
                    <span className="opacity-70">
                      <span className="uppercase tracking-widest opacity-60 mr-0.5">rng</span>
                      {rangeStr}
                    </span>
                    {cls.side ? (
                      <span className="font-bold opacity-90">
                        {cls.side === "high" ? "+" : "−"}{cls.deviationPct.toFixed(0)}%
                      </span>
                    ) : (
                      cls.severity === "ok" && <Check size={11} strokeWidth={3} className="opacity-80" />
                    )}
                  </div>
                  {/* Trend row vs previous test */}
                  {prevV != null && (
                    <div className="flex items-center gap-1 mt-1 text-[9px] font-mono-data opacity-70">
                      <TrendIcon size={10} className={trendTone} />
                      <span className={trendTone}>
                        vs prev {fmtNum(prevV, 2)}
                        {deltaFromPrev != null && (
                          <span className="ml-0.5">
                            ({deltaFromPrev > 0 ? "+" : ""}{fmtNum(deltaFromPrev, 2)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {pctChip}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Iron-Triad chart — Hb + Ferritin + Transferrin Sat over time.
          Most sailing-relevant marker triad: storage iron drives endurance. */}
      <IronTriadChart records={labRecords} sailorName={detail.name} />

      {/* Daily wellness check-ins + Readiness score trend. */}
      <WellnessSection sailorId={sailorId} sailorName={detail.name} />

      {/* Injuries panel — sits above the trend chart so on-rest status is the
          first thing a coach sees when opening an athlete's record. */}
      <InjuriesPanel
        key={injuryReloadKey}
        sailor={sailorSummary}
        constants={injuryConstants}
        onChanged={() => {
          setInjuryReloadKey((k) => k + 1);
          onInjuriesChanged?.();
        }}
      />

      <InjuryFormModal
        open={injuryAddOpen}
        onClose={() => setInjuryAddOpen(false)}
        sailor={sailorSummary}
        injury={null}
        constants={injuryConstants}
        onSaved={() => {
          setInjuryReloadKey((k) => k + 1);
          onInjuriesChanged?.();
        }}
      />

      {/* Trend chart with parameter chips */}
      <div className="card p-5 md:p-6" ref={trendRef} data-testid="trend-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Performance over time</div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {selectedParams.length} parameters · {labRecords.length} timepoints
            </div>
          </div>
          <div className="flex items-center gap-2" data-no-print="true">
            <span className="text-[11px] text-slate-500 font-mono-data hidden md:inline">
              Click pills to add / remove (max 6)
            </span>
            <PrintButton
              getNode={() => trendRef.current}
              filename={`${detail.name.replace(/\s+/g, "_")}_trend.png`}
              label="Print graph"
            />
            <WhatsAppShareReportButton
              getNode={() => trendRef.current}
              filename={`${detail.name.replace(/\s+/g, "_")}_trend.png`}
              athleteName={detail.name}
              reportLabel="performance trend chart"
              getMessageLines={() => [
                `Parameters: ${selectedParams.map((k) => (parameters.find((p) => p.key === k) || {}).label || k).join(", ")}`,
                `Data points: ${labRecords.length}`,
              ]}
              familyContacts={detail?.family_contacts || []}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 max-h-32 overflow-y-auto">
          {(parameters || []).map((p) => {
            const active = selectedParams.includes(p.key);
            return (
              <div key={p.key} className="inline-flex items-center gap-1">
                <button
                  onClick={() => {
                    if (active) {
                      setSelectedParams(selectedParams.filter((x) => x !== p.key));
                    } else if (selectedParams.length < 6) {
                      setSelectedParams([...selectedParams, p.key]);
                    }
                  }}
                  className={`pill ${active ? "active" : ""}`}
                  data-testid={`param-pill-${p.key}`}
                >
                  {p.label}
                </button>
                <ParamInfo
                  paramKey={p.key}
                  low={p.range_low}
                  high={p.range_high}
                />
              </div>
            );
          })}
        </div>

        {/* Reference-range strip: makes the limits explicit and impossible
            to miss. The chart itself also marks them with dashed lines. */}
        <div
          className="flex flex-wrap gap-2 mb-4 text-[11px] font-mono-data"
          data-testid="trend-limits-strip"
        >
          {selectedParams.map((k, i) => {
            const p = (parameters || []).find((x) => x.key === k);
            if (!p) return null;
            const lo = p.range_low;
            const hi = p.range_high;
            const color = SERIES_COLORS[i % SERIES_COLORS.length];
            return (
              <div
                key={k}
                className="flex items-center gap-2 px-2.5 py-1 rounded-md border border-[#1E304B] bg-[#0E1A2E]"
                title={`${p.label} reference range`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-slate-300">{p.label}</span>
                <ParamInfo paramKey={p.key} low={lo} high={hi} />
                <span className="text-slate-500">{p.unit && `(${p.unit})`}</span>
                <span className="text-slate-500">·</span>
                {lo != null ? (
                  <>
                    <span className="text-amber-300">Low</span>
                    <span className="text-slate-200">{fmtNum(lo, 2)}</span>
                  </>
                ) : (
                  <span className="text-slate-600 italic">no low limit</span>
                )}
                <span className="text-slate-600">→</span>
                {hi != null ? (
                  <>
                    <span className="text-rose-300">High</span>
                    <span className="text-slate-200">{fmtNum(hi, 2)}</span>
                  </>
                ) : (
                  <span className="text-slate-600 italic">no high limit</span>
                )}
              </div>
            );
          })}
        </div>

        {selectedParams.length === 1 ? (
          <TrendChart
            data={chartData}
            series={series}
            range={(() => {
              const p = (parameters || []).find((x) => x.key === selectedParams[0]);
              return p ? { low: p.range_low, high: p.range_high } : undefined;
            })()}
            yLabel={(parameters || []).find((x) => x.key === selectedParams[0])?.unit}
            height={360}
            testid="sailor-trend-chart"
          />
        ) : (
          <TrendChart
            data={chartData}
            series={series}
            height={360}
            testid="sailor-trend-chart"
          />
        )}
      </div>

      {showReport && (
        <MedicalHistoryReport
          sailorId={sailorId}
          parameters={parameters}
          initialMode={reportMode}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}
